import clsx from 'clsx';
import { Check, Copy, Loader2 } from 'lucide-react';
import { type ReactNode, useEffect, useState } from 'react';
import { KEEP } from '../../lib/usage.ts';

/**
 * `danger`: a destructive decision the screen is asking for (Decline beside Approve).
 * `undo`: a destructive verb merely available, e.g. Remove on each roster row.
 */
const TONES = {
  primary: 'bg-accent text-canvas hover:opacity-90',
  accent: 'border border-ink/40 text-ink hover:border-ink/70 hover:bg-ink/10',
  danger: 'bg-down text-canvas hover:opacity-90',
  undo: 'border border-line-soft text-ink-muted hover:border-down/70 hover:text-down',
  quiet: 'border border-line-soft text-ink-muted hover:border-line hover:text-ink',
} as const;

export type Tone = keyof typeof TONES;

const SIZES = {
  sm: 'rounded-control px-2 py-0.5 text-xs touch:min-h-8 touch:px-2.5',
  md: 'rounded-control px-2.5 py-1 text-xs touch:min-h-9 touch:px-3.5',
  lg: 'rounded-control px-4 py-2.5 text-sm touch:min-h-11',
} as const;

const BUTTON = 'inline-flex items-center gap-1.5 font-medium whitespace-nowrap transition-colors';

/** Only for elements that cannot be `Button`: a form submit (keeps Enter), a file-picker label or a link. */
export const buttonClass = (tone: Tone = 'quiet', size: keyof typeof SIZES = 'md') =>
  clsx(BUTTON, KEEP, SIZES[size], TONES[tone], 'disabled:opacity-45');

export function Button({
  tone = 'quiet',
  size = 'md',
  busy = false,
  disabled = false,
  onClick,
  children,
  title,
  redacted = false,
}: {
  tone?: Tone;
  size?: keyof typeof SIZES;
  busy?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
  title?: string;
  /** The label is a figure: keep it out of session recordings. */
  redacted?: boolean;
}) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled || busy}
      onClick={onClick}
      className={clsx(BUTTON, !redacted && KEEP, SIZES[size], TONES[tone], 'disabled:opacity-45')}
    >
      {busy && <Loader2 aria-hidden className="size-3 animate-spin" />}
      {children}
    </button>
  );
}

export function RowAction({
  on = false,
  tone = 'quiet',
  onClick,
  children,
}: {
  on?: boolean;
  tone?: Tone;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-expanded={on}
      onClick={onClick}
      className={clsx(
        BUTTON,
        SIZES.sm,
        on ? 'border border-line bg-surface-raised text-ink' : TONES[tone],
      )}
    >
      {children}
    </button>
  );
}

/** `navigator.clipboard` is absent on insecure origins and can be refused; never fail silently. */
export function CopyButton({ text, label = 'Copy URL' }: { text: string; label?: string }) {
  const [said, setSaid] = useState<'copy' | 'copied' | 'failed'>('copy');

  useEffect(() => {
    if (said === 'copy') return;
    const timer = setTimeout(() => setSaid('copy'), 3000);
    return () => clearTimeout(timer);
  }, [said]);

  const copy = () => {
    const wrote = navigator.clipboard?.writeText(text);
    if (!wrote) {
      setSaid('failed');
      return;
    }
    void wrote.then(() => setSaid('copied')).catch(() => setSaid('failed'));
  };

  return (
    <Button tone={said === 'copied' ? 'quiet' : 'primary'} onClick={copy}>
      {said === 'copied' ? (
        <Check aria-hidden className="size-3.5" />
      ) : (
        <Copy aria-hidden className="size-3.5" />
      )}
      {said === 'copied' ? 'Copied' : said === 'failed' ? 'Select it and copy' : label}
    </Button>
  );
}
